import { NodePath } from '@babel/traverse';
import * as t from '@babel/types';

function isNodePath<T extends t.BaseNode>(
  fn: (node: object, ...args: any[]) => node is T,
): (path: NodePath<t.BaseNode>) => path is NodePath<T> {
  return (path: NodePath<t.BaseNode>): path is NodePath<T> => {
    return fn(path.node);
  };
}

function isNonNullNodePath<T extends t.BaseNode | t.BaseNode[]>(
  path: NodePath<T | null>,
): path is NodePath<T> {
  return path.node != null;
}

const convertFlowTypeParameter = (path: NodePath<t.TypeParameter>): t.TSTypeParameter => {
  const bound = path.get('bound');
  const def = path.get('default') as NodePath<t.FlowType | null>;

  const typeParameter = t.tsTypeParameter(
    isNonNullNodePath(bound) ? convertFlowType(bound.get('typeAnnotation')) : null,
    isNonNullNodePath(def) ? convertFlowType(def) : null,
  );
  typeParameter.name = path.node.name;
  return typeParameter;
};

function convertTypeParameters(
  path: NodePath<t.TypeParameterDeclaration>,
): t.TSTypeParameterDeclaration;
function convertTypeParameters(
  path: NodePath<null>,
): null;
function convertTypeParameters(
  path: NodePath<t.TypeParameterDeclaration | null>,
): t.TSTypeParameterDeclaration | null;
function convertTypeParameters(
  path: NodePath<t.TypeParameterDeclaration | null>,
): t.TSTypeParameterDeclaration | null {
  if (path.node == null) {
    return null;
  }

  return t.tsTypeParameterDeclaration(
    path.node.params.map((_, i) => convertFlowTypeParameter(path.get(`params.${i}`))),
  );
}

const convertTypeParameterInstantiation = (
  path: NodePath<t.TypeParameterInstantiation | null>,
): t.TSTypeParameterInstantiation | null => {
  if (path.node == null) {
    return null;
  }

  return t.tsTypeParameterInstantiation(
    path.node.params.map((_, i) => convertFlowType(path.get(`params.${i}`))),
  );
};

const convertParameter = (path: NodePath<t.FunctionTypeParam>, idx: number): t.Identifier => {
  const identifier = t.identifier(path.node.name == null ? `param${idx}` : path.node.name.name);
  identifier.optional = path.node.optional;
  identifier.typeAnnotation = t.tsTypeAnnotation(convertFlowType(path.get('typeAnnotation')));
  return identifier;
};

const convertParameters = (
  path: NodePath<t.FunctionTypeAnnotation>,
): Array<t.Identifier | t.RestElement> | null => {
  const result: Array<t.Identifier | t.RestElement> = path.node.params.map(
    (_, idx) => convertParameter(path.get(`params.${idx}`), idx),
  );

  const rest = path.get('rest');
  if (isNonNullNodePath(rest) && rest.node.name != null) {
    result.push(
      t.restElement(rest.node.name, null, t.tsTypeAnnotation(convertFlowType(rest.get('typeAnnotation')))),
    );
  }

  return result;
};

const convertProperty = (prop: NodePath<t.ObjectTypeProperty>) => {
  const { key, optional } = prop.node;
  const id = t.isIdentifier(key) ? t.identifier(key.name) : t.stringLiteral(key.value);
  const value = prop.get('value');
  if ((prop.node as any).method && isNodePath(t.isFunctionTypeAnnotation)(value)) {
    const method = t.tsMethodSignature(
      id,
      convertTypeParameters(value.get('typeParameters')),
      convertParameters(value),
      t.tsTypeAnnotation(convertFlowType(value.get('returnType'))),
    );
    method.optional = optional;
    method.loc = prop.node.loc;
    return method;
  }

  const property = t.tsPropertySignature(
    id,
    t.tsTypeAnnotation(convertFlowType(value)),
    null,
  );
  property.optional = optional;
  property.readonly = prop.node.variance != null && prop.node.variance.kind === 'plus';
  property.loc = prop.node.loc;

  return property;
};

const convertProperties = (
  path: NodePath<t.ObjectTypeAnnotation>,
) => path.node.properties.map((_, idx) => {
  const prop = path.get(`properties.${idx}`);
  if (isNodePath(t.isObjectTypeProperty)(prop)) {
    return convertProperty(prop);
  }

  return null;
}).filter(isNotNull);

const convertCallProperty = (path: NodePath<t.ObjectTypeCallProperty>) => {
  const value = path.get('value');
  if (!isNodePath(t.isFunctionTypeAnnotation)(value)) {
    throw new Error(`Unexpected object call type: ${value.type}`);
  }
  const decl = t.tsCallSignatureDeclaration(
    convertTypeParameters(value.get('typeParameters')),
    convertParameters(value),
    t.tsTypeAnnotation(convertFlowType(value.get('returnType'))),
  );
  decl.loc = path.node.loc;
  return decl;
};

const convertCallProperties = (
  path: NodePath<t.ObjectTypeAnnotation>,
) => path.node.callProperties != null
    ? path.node.callProperties.map((_, idx) => convertCallProperty(path.get(`callProperties.${idx}`)))
    : [];

const convertIndexer = (indexer: NodePath<t.ObjectTypeIndexer>) => {
  const indexerID = indexer.node.id;
  const id = t.identifier(indexerID == undefined ? 'key' : indexerID.name);
  const type = t.isNumberTypeAnnotation(indexer.node.key) ? t.tsNumberKeyword() : t.tsStringKeyword();
  id.typeAnnotation = t.tsTypeAnnotation(type);
  const signature = t.tsIndexSignature(
    [id],
    t.tsTypeAnnotation(convertFlowType(indexer.get('value'))),
  );
  signature.readonly = indexer.node.variance != null && indexer.node.variance.kind === 'plus';
  signature.loc = indexer.node.loc;
  return signature;
};

const convertIndexers = (
  path: NodePath<t.ObjectTypeAnnotation>,
) => path.node.indexers != null
    ? path.node.indexers.map((_, idx) => convertIndexer(path.get(`indexers.${idx}`)))
    : [];

const convertAllProperties = (
  path: NodePath<t.ObjectTypeAnnotation>,
) =>
  (convertProperties(path) as t.TSTypeElement[]).concat(
    convertCallProperties(path),
  ).concat(
    convertIndexers(path),
  );

const convertQualifiedTypeIdentifier = (id: t.QualifiedTypeIdentifier): t.TSEntityName =>
  t.tsQualifiedName(
    t.isIdentifier(id.qualification) ? t.identifier(id.qualification.name) : convertQualifiedTypeIdentifier(id),
    t.identifier(id.id.name)
  );

const convertFlowType = (path: NodePath<t.FlowType>): t.TSType => {
  if (isNodePath(t.isAnyTypeAnnotation)(path)) {
    return t.tsAnyKeyword();
  }

  if (isNodePath(t.isArrayTypeAnnotation)(path)) {
    return t.tsArrayType(convertFlowType(path.get('elementType')));
  }

  if (isNodePath(t.isBooleanTypeAnnotation)(path)) {
    return t.tsBooleanKeyword();
  }

  if (isNodePath(t.isBooleanLiteralTypeAnnotation)(path)) {
    return t.tsLiteralType(t.booleanLiteral(path.node.value));
  }

  if (isNodePath(t.isNullLiteralTypeAnnotation)(path)) {
    return t.tsUndefinedKeyword();
  }

  if (isNodePath(t.isExistsTypeAnnotation)(path)) {
    return t.tsAnyKeyword();
  }

  if (isNodePath(t.isFunctionTypeAnnotation)(path)) {
    const func = t.tsFunctionType(
      convertTypeParameters(path.get('typeParameters')),
      t.tsTypeAnnotation(convertFlowType(path.get('returnType'))),
    );
    func.parameters = convertParameters(path);
    return t.tsParenthesizedType(func);
  }

  if (isNodePath(t.isGenericTypeAnnotation)(path)) {
    const tsTypeParameters = convertTypeParameterInstantiation(path.get('typeParameters'));
    const id = path.node.id;
    if (id.name === '$FlowFixMe') {
      return t.tsAnyKeyword();
    } else if (id.name === '$Keys') {
      return t.tsTypeOperator(tsTypeParameters!.params[0], 'keyof');
    } else if (id.name === '$Values') {
      const tsType = tsTypeParameters!.params[0];
      return t.tsIndexedAccessType(tsType, t.tsTypeOperator(tsType, 'keyof'));
    } else if (id.name === '$ReadOnly') {
      return t.tsTypeReference(t.identifier('Readonly'), tsTypeParameters);
    } else if (id.name === '$ReadOnlyArray') {
      return t.tsTypeReference(t.identifier('ReadonlyArray'), tsTypeParameters);
    } else if (id.name === '$Exact') {
      return tsTypeParameters!.params[0];
    } else if (id.name === '$Diff') {
      const [tsX, tsY] = tsTypeParameters!.params;
      const tsKeyofX = t.tsTypeOperator(tsX, 'keyof');
      const tsKeyofY = t.tsTypeOperator(tsY, 'keyof');
      const tsExclude = t.tsTypeReference(
        t.identifier('Exclude'),
        t.tsTypeParameterInstantiation([tsKeyofX, tsKeyofY]),
      );
      return t.tsTypeReference(t.identifier('Pick'), t.tsTypeParameterInstantiation([tsX, tsExclude]));
    } else if (id.name === '$PropertyType' || id.name === '$ElementType') {
      const [tsT, tsK] = tsTypeParameters!.params;
      return t.tsIndexedAccessType(tsT, tsK);
    } else if (id.name === 'Array') {
      const [ts] = tsTypeParameters!.params;
      if (!(
        t.isTSUnionType(ts) ||
        t.isTSIntersectionType(ts) ||
        t.isTSTypeLiteral(ts) ||
        t.isTSTupleType(ts)
      )) {
        return t.tsArrayType(ts);
      }
    } else if (t.isQualifiedTypeIdentifier(id)) {
      return t.tsTypeReference(
        convertQualifiedTypeIdentifier(id),
        tsTypeParameters,
      );
    } else if (id.name === 'Object') {
      return t.tsAnyKeyword();
    }

    return t.tsTypeReference(t.identifier(id.name), tsTypeParameters);
  }

  if (isNodePath(t.isInterfaceTypeAnnotation)(path)) {
    // Not really sure what this is
    throw new Error('Not Implemented');
  }

  if (isNodePath(t.isIntersectionTypeAnnotation)(path)) {
    return t.tsIntersectionType(path.node.types.map((_, idx) => convertFlowType(path.get(`types.${idx}`))));
  }

  if (isNodePath(t.isMixedTypeAnnotation)(path)) {
    return t.tsTypeLiteral([]);
  }

  if (isNodePath(t.isEmptyTypeAnnotation)(path)) {
    return t.tsNeverKeyword();
  }

  if (isNodePath(t.isNullableTypeAnnotation)(path)) {
    return t.tsUnionType([convertFlowType(path.get('typeAnnotation')), t.tsUndefinedKeyword()]);
  }

  if (isNodePath(t.isNumberLiteralTypeAnnotation)(path)) {
    return t.tsLiteralType(t.numericLiteral(path.node.value));
  }

  if (isNodePath(t.isNumberTypeAnnotation)(path)) {
    return t.tsNumberKeyword();
  }

  if (isNodePath(t.isObjectTypeAnnotation)(path)) {
    const members: t.TSTypeElement[] = convertAllProperties(path);
    const spreads: t.TSType[] = path.node.properties.map((_, idx) => {
      const prop = path.get(`properties.${idx}`);
      if (isNodePath(t.isObjectTypeSpreadProperty)(prop)) {
        return convertFlowType(prop.get('argument'));
      }

      return null;
    }).filter(isNotNull);

    let ret: t.TSType = t.tsTypeLiteral(members);
    ret.loc = path.node.loc;
    if (spreads.length > 0) {
      spreads.unshift(ret);
      ret = t.tsUnionType(spreads);
    }

    return ret;
  }

  if (isNodePath(t.isStringLiteralTypeAnnotation)(path)) {
    return t.tsLiteralType(t.stringLiteral(path.node.value));
  }

  if (isNodePath(t.isStringTypeAnnotation)(path)) {
    return t.tsStringKeyword();
  }

  if (isNodePath(t.isThisTypeAnnotation)(path)) {
    return t.tsThisType();
  }

  if (isNodePath(t.isTupleTypeAnnotation)(path)) {
    return t.tsTupleType(path.node.types.map((_, idx) => convertFlowType(path.get(`types.${idx}`))));
  }

  if (isNodePath(t.isTypeofTypeAnnotation)(path)) {
    const operator = t.tsTypeOperator(convertFlowType(path.get('argument')));
    operator.operator = 'typeof';
    return operator;
  }

  if (isNodePath(t.isUnionTypeAnnotation)(path)) {
    return t.tsParenthesizedType(t.tsUnionType(path.node.types.map((_, idx) => convertFlowType(path.get(`types.${idx}`)))));
  }

  if (isNodePath(t.isVoidTypeAnnotation)(path)) {
    return t.tsVoidKeyword();
  }

  throw new Error(`Unsupported FlowType(type=${path.node.type})`);
};

function isNotNull<T>(value: T | null): value is T {
  return value != null;
}

const convertInterface = (
  id: t.Identifier,
  typeParametersIn: NodePath<t.TypeParameterDeclaration | null>,
  body: NodePath<t.ObjectTypeAnnotation>,
  additionalExtends: t.TSExpressionWithTypeArguments[] = [],
): t.BaseNode => {
  const typeParameters = convertTypeParameters(typeParametersIn);
  const extend = body.node.properties
    .map((_, idx) => {
      const prop = body.get(`properties.${idx}`);
      if (isNodePath(t.isObjectTypeSpreadProperty)(prop)) {
        const argument = prop.get('argument');
        if (isNodePath(t.isGenericTypeAnnotation)(argument)) {
          const tsTypeParameters = convertTypeParameterInstantiation(argument.get('typeParameters'));
          return t.tsExpressionWithTypeArguments(t.identifier(argument.node.id.name), tsTypeParameters);
        }
      }
      return null;
    })
    .filter(isNotNull);
  const bodyOut = t.tsInterfaceBody(convertAllProperties(body));
  const allExtends = additionalExtends.concat(extend);

  const decl = t.tsInterfaceDeclaration(id, typeParameters, allExtends.length === 0 ? null : allExtends, bodyOut);
  decl.loc = id.loc;
  return decl;
};

const convertInterfaceExtends = (
  path: NodePath<t.InterfaceExtends>,
): t.TSExpressionWithTypeArguments => {
  const tsTypeParameters = convertTypeParameterInstantiation(path.get('typeParameters'));
  return t.tsExpressionWithTypeArguments(t.identifier(path.node.id.name), tsTypeParameters);
}

const convertInterfaceDeclaration = (path: NodePath<t.InterfaceDeclaration>) => convertInterface(
  path.node.id,
  path.get('typeParameters'),
  path.get('body'),
  (path.node as any).extends == null 
    ? [] 
    : (path.node as any).extends.map((_: any, idx: number) => convertInterfaceExtends(path.get(`extends.${idx}`))),
);

const convertObjectTypeAnnotationAlias = (
  path: NodePath<t.TypeAlias>,
  right: NodePath<t.ObjectTypeAnnotation>,
): t.BaseNode =>
  convertInterface(
    path.node.id,
    path.get('typeParameters'),
    right,
  );

export const visitor = {
  ImportDeclaration(path: NodePath<t.ImportDeclaration>): void {
    if (
      (
        path.node.source.value.startsWith('./') ||
        path.node.source.value.startsWith('../')
      ) &&
      path.node.specifiers.length >= 1
    ) {
      const index = path.node.specifiers.findIndex((element) => t.isImportDefaultSpecifier(element));
      const specifier = path.node.specifiers.find((element) => t.isImportDefaultSpecifier(element));
      if (specifier != null && t.isImportDefaultSpecifier(specifier)) {
        const newSpecifier = t.importSpecifier(specifier.local, specifier.local);
        const specifiers = [...path.node.specifiers];
        specifiers[index] = newSpecifier;
        path.replaceWith(t.importDeclaration(specifiers, path.node.source));
        return;
      }
    }

    if ((path.node as any).importKind != null) {
      const node = t.importDeclaration(path.node.specifiers, path.node.source);
      path.replaceWith(node);
    }
  },
  ImportSpecifier(path: NodePath<t.ImportSpecifier>): void {
    if ((path.node as any).importKind === 'type') {
      path.replaceWith(t.importSpecifier(path.node.local, path.node.imported));
    }
  },
  InterfaceDeclaration(path: NodePath<t.InterfaceDeclaration>): void {
    path.replaceWith(convertInterfaceDeclaration(path));
  },
  OpaqueType(path: NodePath<t.OpaqueType>): void {
    const tsNode = t.tsTypeAliasDeclaration(path.node.id, null, convertFlowType(path.get('impltype')));
    tsNode.declare = false;

    path.replaceWith(tsNode);
  },
  TypeAlias(path: NodePath<t.TypeAlias>): void {
    const right = path.get('right');
    if (isNodePath(t.isObjectTypeAnnotation)(right)) {
      path.replaceWith(convertObjectTypeAnnotationAlias(path, right));
    } else {
      path.replaceWith(
        t.tsTypeAliasDeclaration(
          t.identifier(path.node.id.name),
          convertTypeParameters(path.get('typeParameters')),
          convertFlowType(right),
        ),
      );
    }
  },
  TypeAnnotation(path: NodePath<t.TypeAnnotation>): void {
    path.replaceWith(t.tsTypeAnnotation(convertFlowType(path.get('typeAnnotation'))));
  },
  TypeCastExpression(path: NodePath<t.TypeCastExpression>): void {
    path.replaceWith(
      t.tsAsExpression(
        t.parenthesizedExpression(path.node.expression),
        convertFlowType(path.get('typeAnnotation').get('typeAnnotation')),
      ),
    );
  },
  TypeParameterDeclaration(path: NodePath<t.TypeParameterDeclaration>): void {
    path.replaceWith(convertTypeParameters(path));
  },
  ClassProperty(path: NodePath<t.ClassProperty>): void {
    const { key } = path.node;
    if (t.isIdentifier(key)) {
      if (key.name.startsWith('__')) {
        const prop = t.classProperty(
          t.identifier(`${key.name.slice(2)}Internal`),
          path.node.value,
        );
        prop.accessibility = 'private';
        prop.readonly = true;
        // @ts-ignore
        prop.static = path.node.static;
        prop.typeAnnotation = path.node.typeAnnotation;
        path.replaceWith(prop);
      } else if (key.name.startsWith('_')) {
        const prop = t.classProperty(
          t.identifier(key.name.slice(1)),
          path.node.value,
        );
        prop.accessibility = 'private';
        prop.readonly = true;
        // @ts-ignore
        prop.static = path.node.static;
        prop.typeAnnotation = path.node.typeAnnotation;
        path.replaceWith(prop);
      } else if (path.node.accessibility == null) {
        const prop = t.classProperty(t.identifier(key.name), path.node.value);
        prop.accessibility = 'public';
        prop.readonly = true;
        // @ts-ignore
        prop.static = path.node.static;
        prop.typeAnnotation = path.node.typeAnnotation;
        path.replaceWith(prop);
      }
    }
  },
  ClassMethod(path: NodePath<t.ClassMethod>): void {
    const { key } = path.node;
    if (t.isIdentifier(key)) {
      let id: string;
      let accessibility: 'private' | 'public';
      if (key.name.startsWith('__')) {
        id = `${key.name.slice(2)}Internal`;
        accessibility = 'private';
      } else if (key.name.startsWith('_')) {
        id = key.name.slice(1);
        accessibility = 'private';
      } else if (path.node.accessibility == null && key.name !== 'constructor') {
        id = key.name;
        accessibility = 'public';
      } else {
        return;
      }

      const method = t.classMethod(
        path.node.kind,
        t.identifier(id),
        path.node.params,
        path.node.body,
      );
      method.accessibility = accessibility;
      method.async = path.node.async;
      method.computed = path.node.computed;
      method.decorators = path.node.decorators;
      method.generator = path.node.generator;
      method.innerComments = path.node.innerComments;
      method.leadingComments = path.node.leadingComments;
      method.optional = path.node.optional;
      method.returnType = path.node.returnType;
      method.trailingComments = path.node.trailingComments;
      method.typeParameters = path.node.typeParameters;
      method.loc = path.node.loc;
      // @ts-ignore
      method.static = path.node.static;

      path.replaceWith(method);
    }
  },
  MemberExpression(path: NodePath<t.MemberExpression>): void {
    const prop = path.get('property');
    if (isNodePath(t.isIdentifier)(prop)) {
      if (prop.node.name.startsWith('__')) {
        prop.replaceWith(t.identifier(`${prop.node.name.slice(2)}Internal`));
      } else if (prop.node.name.startsWith('_')) {
        prop.replaceWith(t.identifier(prop.node.name.slice(1)));
      }
    }
  },
  ExportDefaultDeclaration(path: NodePath<t.ExportDefaultDeclaration>): void {
    const decl = path.get('declaration');
    if (isNodePath(t.isClassDeclaration)(decl)) {
      const exportNamed = t.exportNamedDeclaration(decl.node, []);
      exportNamed.loc = path.node.loc;
      path.replaceWith(exportNamed);
    }
  },
  ExportNamedDeclaration(path: NodePath<t.ExportNamedDeclaration>): void {
    if ((path.node as any).exportKind === 'type') {
      const decl = t.exportNamedDeclaration(path.node.declaration, path.node.specifiers, path.node.source);
      path.replaceWith(decl);
    }
  },
};
